import React from "react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useHistory } from "react-router";
import axios from "axios";
import { baseUrl, baseUrlCSRFCookie } from "../../../resources/Constants";
import logo from '../../../resources/img/smileweb.png';
import ReCAPTCHA from "react-google-recaptcha";
import ModalActualizarUsuario from "./ModalActualizarUsuario";
import './Login2.css';

const DOC_TYPES = ['CC', 'CE', 'NIT', 'PP'];

const PasswordInput = ({
    id,
    name,
    value,
    onChange,
    disabled,
    placeholder,
    autoComplete,
    className = 'form-control form-control-sm',
}) => {
    const [visible, setVisible] = useState(false);

    return (
        <div className="input-group input-group-sm login-cvc-input">
            <input
                id={id}
                name={name}
                type={visible ? 'text' : 'password'}
                className={className}
                value={value}
                onChange={onChange}
                disabled={disabled}
                placeholder={placeholder}
                autoComplete={autoComplete}
            />
            <div className="input-group-append">
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setVisible((v) => !v)}
                    disabled={disabled}
                    aria-label={visible ? 'Ocultar CVC' : 'Mostrar CVC'}
                    tabIndex={-1}
                >
                    <i className={`fas fa-eye${visible ? '-slash' : ''}`} />
                </button>
            </div>
        </div>
    );
};

const initialPaymentMethodForm = {
    doc_type: 'CC',
    doc_number: '',
    card_number: '',
    card_exp_month: '',
    card_exp_year: '',
    card_cvc: '',
};

// Instancia axios para hacer solicitud del CSRF Cookie al backend
const axiosInstanceCSRFCookie = axios.create({
    baseURL: baseUrlCSRFCookie,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
    },
    withCredentials: true,
    withXSRFToken: true,
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
});

// Instancia axios para autenticación
const axiosInstance = axios.create({
    baseURL: baseUrl,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
    },
    withCredentials: true,
    withXSRFToken: true,
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
});

function parseEpaycoReturnFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const refPayco = params.get('ref_payco') || params.get('x_ref_payco');
    const codResponse = params.get('x_cod_response') || params.get('x_response');
    if (!refPayco) {
        return null;
    }
    const accepted = codResponse === '1' || String(codResponse || '').toLowerCase() === 'accepted';
    return { refPayco, accepted };
}

function Login2(props) {
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [feedbackType, setFeedbackType] = useState('invalid-feedback');
    const [msg, setMsg] = useState('');
    const [captchaValue, setCaptchaValue] = useState(null);
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [showChangePaymentForm, setShowChangePaymentForm] = useState(false);
    const [paymentMethodForm, setPaymentMethodForm] = useState(initialPaymentMethodForm);
    const [paymentActionMsg, setPaymentActionMsg] = useState('');
    const [paymentActionMsgType, setPaymentActionMsgType] = useState('info');
    const [paymentReturn, setPaymentReturn] = useState(null);

    const [showActualizarModal, setShowActualizarModal] = useState(false);
    const [actualizarData, setActualizarData] = useState(null);

    let history = useHistory();

    useEffect(() => {
        const epaycoReturn = parseEpaycoReturnFromUrl();
        if (epaycoReturn) {
            setPaymentReturn(epaycoReturn);
            if (window.history.replaceState) {
                const cleanUrl = window.location.pathname + window.location.hash;
                window.history.replaceState({}, document.title, cleanUrl);
            }
        }
    }, []);

    const onCaptchaChange = (value) => {
        setCaptchaValue(value);
    };

    const clearPaymentOptions = () => {
        setPaymentInfo(null);
        setShowChangePaymentForm(false);
        setPaymentActionMsg('');
        setPaymentMethodForm(initialPaymentMethodForm);
    };

    const handleLoginChange = (e) => {
        setLogin(e.target.value);
        clearPaymentOptions();
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        clearPaymentOptions();
    };

    const credentialsPayload = () => {
        const isEmail = String(login).includes('@');
        return isEmail
            ? { email: login, password }
            : { login, password };
    };

    const handlePaymentMethodFieldChange = (e) => {
        const { name, value } = e.target;
        setPaymentMethodForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleRetryCharge = async () => {
        if (!paymentInfo?.credentials_verified || processingPayment) {
            return;
        }
        if (!paymentInfo?.can_retry_charge) {
            setPaymentActionMsgType('warning');
            setPaymentActionMsg(paymentInfo?.retry_charge_reason || 'No puede reintentar el pago en este momento.');
            return;
        }

        setProcessingPayment(true);
        setPaymentActionMsg('');
        try {
            await axiosInstanceCSRFCookie.get('/sanctum/csrf-cookie');
            const res = await axiosInstance.post('/epayco/subscription/retry-charge-with-credentials', credentialsPayload());
            const approved = res.data?.charge?.charge_approved
                || res.data?.debug?.summary?.charge_approved;
            const rejected = res.data?.charge?.charge_status === 'rejected'
                || res.data?.debug?.summary?.charge_status === 'rejected';
            setPaymentActionMsgType(rejected ? 'warning' : approved ? 'success' : 'info');
            setPaymentActionMsg(
                approved
                    ? `${res.data?.message || 'Pago aprobado.'} Ya puede iniciar sesión.`
                    : (res.data?.message || 'Cobro procesado.')
            );
        } catch (err) {
            setPaymentActionMsgType('danger');
            const data = err.response?.data;
            setPaymentActionMsg(data?.message || 'No se pudo reintentar el pago.');
        } finally {
            setProcessingPayment(false);
        }
    };

    const handleUpdatePaymentMethod = async () => {
        if (!paymentInfo?.credentials_verified || processingPayment) {
            return;
        }
        if (!paymentInfo?.can_update_payment_method) {
            setPaymentActionMsgType('warning');
            setPaymentActionMsg(paymentInfo?.update_payment_method_reason || 'No puede actualizar el método de pago.');
            return;
        }

        setProcessingPayment(true);
        setPaymentActionMsg('');
        try {
            await axiosInstanceCSRFCookie.get('/sanctum/csrf-cookie');
            const res = await axiosInstance.post('/epayco/subscription/update-payment-method-with-credentials', {
                ...credentialsPayload(),
                ...paymentMethodForm,
            });
            const approved = res.data?.charge?.charge_approved
                || res.data?.debug?.summary?.charge_approved;
            const rejected = res.data?.charge?.charge_status === 'rejected'
                || res.data?.debug?.summary?.charge_status === 'rejected';
            setPaymentActionMsgType(rejected ? 'warning' : approved ? 'success' : 'info');
            setPaymentActionMsg(
                approved
                    ? `${res.data?.message || 'Pago aprobado.'} Ya puede iniciar sesión.`
                    : (res.data?.message || 'Método de pago actualizado.')
            );
            if (approved) {
                setShowChangePaymentForm(false);
            }
        } catch (err) {
            setPaymentActionMsgType('danger');
            const data = err.response?.data;
            setPaymentActionMsg(data?.message || 'No se pudo actualizar el método de pago.');
        } finally {
            setProcessingPayment(false);
        }
    };

    const showPaymentOptions = Boolean(
        paymentInfo?.code === 'PENDING_PAYMENT'
        && paymentInfo?.credentials_verified === true
    );

    const needsSupportResubscribe = Boolean(
        paymentInfo?.needs_support_resubscribe
        || (
            paymentInfo
            && !paymentInfo.epayco_subscription_id
            && paymentInfo.subscription_cancelled_notice
        )
    );

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!captchaValue) {
            setFeedbackType('invalid-feedback visible');
            setMsg('Por favor, completa el captcha');
            return;
        }

        setLoading(true);
        clearPaymentOptions();

        const payload = credentialsPayload();

        axiosInstanceCSRFCookie.get('/sanctum/csrf-cookie')
            .then(() => axiosInstance.post('/login', payload))
            .then((response) => {
                setMsg('');
                setLoading(false);

                if (response.data.requiere_actualizacion) {
                    setActualizarData(response.data);
                    setShowActualizarModal(true);
                    return;
                }

                const status = response.data.user_status;
                if (status != null && String(status) !== '1') {
                    setFeedbackType('invalid-feedback visible');
                    setMsg('Su usuario no tiene acceso activo al sistema. Contacte al administrador de la clínica.');
                    return;
                }

                localStorage.setItem('userdata', JSON.stringify(response.data));
                history.push('/home');
            })
            .catch((err) => {
                setFeedbackType('invalid-feedback visible');
                clearPaymentOptions();

                if (err.response) {
                    const data = err.response.data;
                    const apiMsg = data?.message;
                    const status = err.response.status;

                    if (status === 403 && data?.code === 'PENDING_PAYMENT' && data?.credentials_verified) {
                        setMsg(apiMsg || 'Cuenta pendiente de pago.');
                        setPaymentInfo(data);
                        setFeedbackType('invalid-feedback visible login-msg-pending');
                    } else if (status === 403 && apiMsg) {
                        setMsg(apiMsg);
                    } else {
                        setMsg(apiMsg || 'No se pudo iniciar sesión. Verifica usuario y contraseña.');
                    }
                } else {
                    const isNetwork = err.code === 'ERR_NETWORK' || err.message === 'Network Error';
                    if (process.env.NODE_ENV === 'development') {
                        console.warn('Login: fallo CSRF o red', baseUrlCSRFCookie, err);
                    }
                    setMsg(
                        isNetwork
                            ? `No hay conexión con el backend en ${baseUrlCSRFCookie}. Verifique REACT_APP_API_URL o php artisan serve.`
                            : 'Error de conexión. Intenta nuevamente.'
                    );
                }
                setLoading(false);
            });
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-logo">
                    <img src={logo} alt="SmileWEB Logo" />
                </div>

                <h2 className="login-title">¡Bienvenido a SmileWEB!</h2>
                <p className="login-subtitle">Sistema Integral de Gestión Médica y Odontológica</p>

                {paymentReturn && (
                    <div className={`login-payment-return ${paymentReturn.accepted ? 'is-success' : 'is-neutral'}`}>
                        {paymentReturn.accepted ? (
                            <>
                                <strong><i className="fas fa-check-circle mr-1"></i> Pago registrado</strong>
                                <p className="mb-0 mt-1">
                                    Inicie sesión para continuar.
                                </p>
                            </>
                        ) : (
                            <>
                                <strong>Transacción recibida</strong>
                                <p className="mb-0 mt-1">Verifique el estado del pago e intente ingresar nuevamente.</p>
                            </>
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit} method="post" noValidate className="login-form">
                    <div className="form-group">
                        <label htmlFor="login">Usuario</label>
                        <div className="input-with-icon">
                            <i className="fas fa-user"></i>
                            <input
                                type="text"
                                id="login"
                                className="form-control"
                                placeholder="Ingresa tu usuario"
                                autoFocus={true}
                                onChange={handleLoginChange}
                                value={login}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Contraseña</label>
                        <div className="input-with-icon">
                            <i className="fas fa-lock"></i>
                            <input
                                type="password"
                                id="password"
                                className="form-control"
                                placeholder="Ingresa tu contraseña"
                                onChange={handlePasswordChange}
                                value={password}
                            />
                        </div>
                    </div>

                    <div className="form-group captcha-container">
                        <ReCAPTCHA
                            sitekey={process.env.REACT_APP_RECAPTCHA_SITEKEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                            onChange={onCaptchaChange}
                        />
                    </div>

                    {msg && (
                        <div className={`alert ${msg.includes('exitos') ? 'alert-success' : 'alert-danger'} ${feedbackType.includes('login-msg-pending') ? 'login-msg-pending' : ''}`} role="alert">
                            {msg}
                        </div>
                    )}

                    {showPaymentOptions && (
                        <div className="login-payment-panel" role="region" aria-label="Suscripción Smile Web">
                            <div className="login-payment-panel-header">
                                <span className="login-payment-panel-title">Suscripción Smile Web</span>
                                {paymentInfo.amount > 0 && (
                                    <span className="login-payment-panel-amount">
                                        ${Number(paymentInfo.amount).toLocaleString('es-CO')}
                                    </span>
                                )}
                            </div>

                            {paymentActionMsg && (
                                <div className={`alert alert-${paymentActionMsgType === 'success' ? 'success' : paymentActionMsgType === 'warning' ? 'warning' : 'danger'} py-2 small mb-2`}>
                                    {paymentActionMsg}
                                </div>
                            )}

                            {needsSupportResubscribe ? (
                                <div className="alert alert-warning py-2 small mb-0">
                                    <strong>Sin suscripción en ePayco.</strong>{' '}
                                    Comuníquese con soporte SmileWEB para registrar una nueva inscripción
                                    (el día de cobro será el de la reactivación).
                                </div>
                            ) : (
                                <>
                                    <p className="login-payment-panel-desc">
                                        {paymentInfo.debito_automatico_pausado ? (
                                            <>
                                                <strong>Débito automático pausado.</strong>{' '}
                                                La suscripción ePayco sigue registrada. Reintente el pago o cambie su método de pago.
                                            </>
                                        ) : (
                                            <>
                                                Reintente el pago con su tarjeta actual o actualice su método de pago.
                                            </>
                                        )}
                                        {' '}Se mantiene la misma suscripción ePayco (no cambia su fecha de cobro).
                                    </p>

                                    {paymentInfo.can_retry_charge && (
                                        <button
                                            type="button"
                                            className="btn login-btn-pay"
                                            onClick={handleRetryCharge}
                                            disabled={processingPayment}
                                        >
                                            {processingPayment ? 'Procesando…' : 'Reintentar pago'}
                                        </button>
                                    )}

                                    {!paymentInfo.can_retry_charge && paymentInfo.retry_charge_reason && (
                                        <small className="login-payment-panel-note d-block mb-2">
                                            {paymentInfo.retry_charge_reason}
                                        </small>
                                    )}

                                    {paymentInfo.can_update_payment_method && (
                                        <>
                                            <button
                                                type="button"
                                                className="btn login-btn-subscribe mt-2"
                                                onClick={() => setShowChangePaymentForm((v) => !v)}
                                                disabled={processingPayment}
                                            >
                                                {showChangePaymentForm ? 'Ocultar formulario' : 'Cambiar tarjeta y pagar'}
                                            </button>

                                            {showChangePaymentForm && (
                                                <div className="login-subscription-form mt-2" role="group" aria-label="Cambiar método de pago">
                                                    <div className="row">
                                                        <div className="col-4 form-group mb-2">
                                                            <select name="doc_type" className="form-control form-control-sm" value={paymentMethodForm.doc_type} onChange={handlePaymentMethodFieldChange} disabled={processingPayment}>
                                                                {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                                            </select>
                                                        </div>
                                                        <div className="col-8 form-group mb-2">
                                                            <input name="doc_number" className="form-control form-control-sm" placeholder="Documento" value={paymentMethodForm.doc_number} onChange={handlePaymentMethodFieldChange} required disabled={processingPayment} />
                                                        </div>
                                                        <div className="col-12 form-group mb-2">
                                                            <input name="card_number" className="form-control form-control-sm" placeholder="Número de tarjeta" autoComplete="cc-number" value={paymentMethodForm.card_number} onChange={handlePaymentMethodFieldChange} required disabled={processingPayment} />
                                                        </div>
                                                        <div className="col-2 form-group mb-2">
                                                            <input name="card_exp_month" className="form-control form-control-sm" placeholder="MM" maxLength={2} value={paymentMethodForm.card_exp_month} onChange={handlePaymentMethodFieldChange} required disabled={processingPayment} />
                                                        </div>
                                                        <div className="col-4 form-group mb-2">
                                                            <input name="card_exp_year" className="form-control form-control-sm" placeholder="AAAA" maxLength={4} value={paymentMethodForm.card_exp_year} onChange={handlePaymentMethodFieldChange} required disabled={processingPayment} />
                                                        </div>
                                                        <div className="col-6 form-group mb-2">
                                                            <label className="login-cvc-label small mb-1 d-block">CVV</label>
                                                            <PasswordInput
                                                                id="login_card_cvc"
                                                                name="card_cvc"
                                                                value={paymentMethodForm.card_cvc}
                                                                onChange={handlePaymentMethodFieldChange}
                                                                disabled={processingPayment}
                                                                placeholder="123"
                                                                autoComplete="cc-csc"
                                                            />
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn login-btn-subscribe-submit btn-sm btn-block"
                                                        onClick={handleUpdatePaymentMethod}
                                                        disabled={processingPayment}
                                                    >
                                                        {processingPayment ? 'Procesando…' : 'Actualizar tarjeta y pagar'}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <button type="submit" className="btn-login" disabled={loading}>
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                Ingresando...
                            </>
                        ) : (
                            'Ingresar'
                        )}
                    </button>
                </form>

                <div className="login-links">
                    <Link to={'/request-password'} className="login-link">
                        <i className="fas fa-key"></i> Olvidé mi contraseña
                    </Link>
                </div>
            </div>

            {showActualizarModal && actualizarData && (
                <ModalActualizarUsuario
                    show={showActualizarModal}
                    data={actualizarData}
                    loginOrigin={login}
                    passwordOrigin={password}
                    onHide={() => setShowActualizarModal(false)}
                    onSuccess={() => {
                        setShowActualizarModal(false);
                        setLogin('');
                        setPassword('');
                        setMsg('¡Actualización exitosa! Por favor ingresa con tu correo electrónico y tu nueva contraseña.');
                        setFeedbackType('valid-feedback visible text-success');
                    }}
                />
            )}

        </div>
    );
}

export default Login2;
